import {
	type GridApiCommon,
	type GridFilterModel,
	type GridRowId,
	gridFilteredSortedRowIdsSelector,
} from "@mui/x-data-grid-pro";
import type { RefObject } from "react";
import { useEffect, useMemo, useRef, useState } from "react";

type DataRow = { id?: GridRowId; uuid?: GridRowId; isDummyRow?: boolean };

function rowKey(row: DataRow): GridRowId {
	return (row.id != null ? row.id : row.uuid) as GridRowId;
}

function countVisibleRows(filteredRowIds: GridRowId[], rows: DataRow[]) {
	const dataRowIds = new Set(
		rows
			.filter((row) => !row?.isDummyRow && (row.id != null || row.uuid != null))
			.map(rowKey)
	);

	if (dataRowIds.size === 0) {
		return 0;
	}

	return filteredRowIds.filter((id) => dataRowIds.has(id)).length;
}

function getFilterSyncKey(filterModel?: GridFilterModel) {
	if (!filterModel) {
		return "";
	}

	return JSON.stringify({
		items: filterModel.items,
		quickFilterValues: filterModel.quickFilterValues,
		logicOperator: filterModel.logicOperator,
	});
}

export default function useFilteredDataRowCount(
	apiRef: RefObject<GridApiCommon | null>,
	rows: DataRow[],
	loading: boolean,
	filterModel?: GridFilterModel
) {
	const [visibleRowCount, setVisibleRowCount] = useState(0);
	const rowsRef = useRef(rows);
	const countRef = useRef(0);
	const filterSyncKey = useMemo(() => getFilterSyncKey(filterModel), [filterModel]);

	rowsRef.current = rows;

	useEffect(() => {
		if (loading) {
			if (countRef.current !== 0) {
				countRef.current = 0;
				setVisibleRowCount(0);
			}
			return undefined;
		}

		let unsub: (() => void) | undefined;
		let raf = 0;
		let cancelled = false;

		const sync = () => {
			if (cancelled || !apiRef.current?.state) {
				return;
			}

			const filteredIds = gridFilteredSortedRowIdsSelector(
				apiRef as RefObject<GridApiCommon>
			);
			const next = countVisibleRows(filteredIds, rowsRef.current);

			if (next === countRef.current) {
				return;
			}

			countRef.current = next;
			setVisibleRowCount(next);
		};

		const attach = () => {
			if (cancelled) {
				return;
			}

			if (!apiRef.current?.state) {
				raf = requestAnimationFrame(attach);
				return;
			}

			sync();
			unsub = apiRef.current.store.subscribe(sync);
		};

		attach();

		return () => {
			cancelled = true;
			cancelAnimationFrame(raf);
			unsub?.();
		};
	}, [apiRef, loading, filterSyncKey, rows.length]);

	return { visibleRowCount, visibleRuleCount: visibleRowCount };
}
